import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Segmented, Space, Tag, Typography } from 'antd';
import { Canvas, useFrame, useLoader, useThree, type ThreeEvent } from '@react-three/fiber';
import { useTranslation } from 'react-i18next';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls as ThreeOrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';

const { Text } = Typography;

type Series = 'nms35' | 'nms45';

const MODELS: Record<Series, { url: string; sizeMB: number }> = {
  nms35: { url: '/models/nms35.glb', sizeMB: 3.0 },
  nms45: { url: '/models/nms45.glb', sizeMB: 10.4 },
};

const HIGHLIGHT_COLOR = new THREE.Color('#ff8c00');

interface ModelStats {
  bbox: THREE.Vector3; // 包围盒长×宽×高
  partCount: number;   // 独立 mesh 数(>1 表示装配体)
}

/** 加载 GLB,统计 mesh 数 + 包围盒,把数据通过 callback 传出 */
function GLBModel({
  url,
  onStats,
  onMeshClick,
  highlightedMesh,
}: {
  url: string;
  onStats: (root: THREE.Object3D, stats: ModelStats) => void;
  onMeshClick: (mesh: THREE.Mesh | null) => void;
  highlightedMesh: THREE.Mesh | null;
}) {
  const gltf = useLoader(GLTFLoader, url, (loader) => loader.setMeshoptDecoder(MeshoptDecoder));

  // 统计 + bbox
  useEffect(() => {
    let count = 0;
    gltf.scene.traverse((o) => {
      if ((o as THREE.Mesh).isMesh) count++;
    });
    const box = new THREE.Box3().setFromObject(gltf.scene);
    const size = box.getSize(new THREE.Vector3());
    onStats(gltf.scene, { bbox: size, partCount: count });
  }, [gltf.scene, onStats]);

  // 高亮:当 highlightedMesh 切换时,改 emissive。保存原值便于恢复
  const originalEmissivesRef = useRef<Map<THREE.Mesh, THREE.Color>>(new Map());
  useEffect(() => {
    const orig = originalEmissivesRef.current;
    // 还原其他 mesh
    orig.forEach((color, mesh) => {
      if (mesh !== highlightedMesh) {
        const mat = mesh.material as THREE.MeshStandardMaterial | THREE.MeshPhongMaterial;
        if ('emissive' in mat) mat.emissive.copy(color);
        orig.delete(mesh);
      }
    });
    // 高亮新选中
    if (highlightedMesh) {
      const mat = highlightedMesh.material as THREE.MeshStandardMaterial | THREE.MeshPhongMaterial;
      if ('emissive' in mat && !orig.has(highlightedMesh)) {
        orig.set(highlightedMesh, mat.emissive.clone());
        mat.emissive.copy(HIGHLIGHT_COLOR);
      }
    }
  }, [highlightedMesh]);

  const handleClick = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    const obj = e.object;
    if ((obj as THREE.Mesh).isMesh) {
      onMeshClick(obj as THREE.Mesh);
    }
  };
  const handleMissed = () => onMeshClick(null);

  return (
    <group onClick={handleClick} onPointerMissed={handleMissed}>
      <primitive object={gltf.scene} />
    </group>
  );
}

/** OrbitControls + 模型加载完自动 fit camera */
function CameraRig({ target }: { target: THREE.Object3D | null }) {
  const { camera, gl } = useThree();
  const controlsRef = useRef<ThreeOrbitControls | null>(null);

  useEffect(() => {
    const controls = new ThreeOrbitControls(camera, gl.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controlsRef.current = controls;
    return () => {
      controls.dispose();
      controlsRef.current = null;
    };
  }, [camera, gl]);

  useFrame(() => controlsRef.current?.update());

  useEffect(() => {
    if (!target) return;
    const box = new THREE.Box3().setFromObject(target);
    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z) || 1;

    if (camera instanceof THREE.PerspectiveCamera) {
      const fovRad = camera.fov * (Math.PI / 180);
      const dist = (maxDim / 2) / Math.tan(fovRad / 2) * 1.6;
      camera.position.set(center.x + dist, center.y + dist * 0.6, center.z + dist);
      camera.near = Math.max(dist / 1000, 0.01);
      camera.far = dist * 100;
      camera.updateProjectionMatrix();
      camera.lookAt(center);
    }

    if (controlsRef.current) {
      controlsRef.current.target.copy(center);
      controlsRef.current.minDistance = maxDim * 0.3;
      controlsRef.current.maxDistance = maxDim * 8;
      controlsRef.current.update();
    }
  }, [target, camera]);

  return null;
}

function Lights() {
  return (
    <>
      <ambientLight intensity={0.7} />
      <directionalLight position={[5, 10, 7]} intensity={0.9} />
      <directionalLight position={[-5, -3, -5]} intensity={0.3} />
    </>
  );
}

export default function ModelLibraryPanel() {
  const { t } = useTranslation();
  const [series, setSeries] = useState<Series>('nms35');
  const [target, setTarget] = useState<THREE.Object3D | null>(null);
  const [stats, setStats] = useState<ModelStats | null>(null);
  const [highlightedMesh, setHighlightedMesh] = useState<THREE.Mesh | null>(null);
  const [highlightedInfo, setHighlightedInfo] = useState<{ name: string; size: THREE.Vector3 } | null>(null);
  const model = MODELS[series];

  // 切型号时重置 state
  useEffect(() => {
    setTarget(null);
    setStats(null);
    setHighlightedMesh(null);
    setHighlightedInfo(null);
  }, [series]);

  const handleStats = useCallback((root: THREE.Object3D, s: ModelStats) => {
    setTarget(root);
    setStats(s);
  }, []);

  // 点击 mesh 时同时计算它的包围盒,显示局部尺寸
  const handleMeshClick = useCallback((mesh: THREE.Mesh | null) => {
    setHighlightedMesh(mesh);
    if (mesh) {
      const box = new THREE.Box3().setFromObject(mesh);
      const size = box.getSize(new THREE.Vector3());
      setHighlightedInfo({ name: mesh.name || `mesh-${mesh.id}`, size });
    } else {
      setHighlightedInfo(null);
    }
  }, []);

  const bboxText = useMemo(() => {
    if (!stats) return null;
    const { x, y, z } = stats.bbox;
    return `${x.toFixed(0)} × ${y.toFixed(0)} × ${z.toFixed(0)} mm`;
  }, [stats]);

  const highlightedText = useMemo(() => {
    if (!highlightedInfo) return null;
    const { x, y, z } = highlightedInfo.size;
    return `${x.toFixed(0)} × ${y.toFixed(0)} × ${z.toFixed(0)} mm`;
  }, [highlightedInfo]);

  return (
    <Card
      title={t('models.title')}
      size="small"
      extra={
        <Segmented<Series>
          value={series}
          onChange={(v) => setSeries(v)}
          options={[
            { label: t('models.series.nms35'), value: 'nms35' },
            { label: t('models.series.nms45'), value: 'nms45' },
          ]}
        />
      }
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Space size="small" wrap>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('models.hint', { size: model.sizeMB.toFixed(1) })}
          </Text>
          {bboxText && (
            <Tag color="blue" style={{ fontSize: 12 }}>
              {t('models.bbox', { dims: bboxText })}
            </Tag>
          )}
          {stats && stats.partCount > 1 && (
            <Tag color="green" style={{ fontSize: 12 }}>
              {t('models.parts', { count: stats.partCount })}
            </Tag>
          )}
          {highlightedInfo && (
            <Tag color="orange" style={{ fontSize: 12 }}>
              {t('models.selected', { name: highlightedInfo.name, dims: highlightedText })}
            </Tag>
          )}
        </Space>

        <div
          style={{
            width: '100%',
            height: 'min(560px, calc(100vh - 280px))',
            background: '#f6f8fa',
            borderRadius: 8,
            border: '1px solid #eaecef',
          }}
        >
          <Canvas
            key={series}
            camera={{ position: [100, 50, 100], fov: 40, near: 0.1, far: 10000 }}
            gl={{ antialias: true }}
          >
            <Lights />
            <Suspense fallback={null}>
              <GLBModel
                url={model.url}
                onStats={handleStats}
                onMeshClick={handleMeshClick}
                highlightedMesh={highlightedMesh}
              />
            </Suspense>
            <CameraRig target={target} />
          </Canvas>
        </div>

        <Text type="secondary" style={{ fontSize: 12 }}>
          {t('models.controls')}
        </Text>
      </Space>
    </Card>
  );
}
